import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { DeployChangelogRequest, DeployChangelogResponse } from "./types";

import { triggerConfetti } from "@/utils/confetti";
import { API_URL, ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { ToastType } from "@/enums";

interface UseDeployChangelogParams {
  changelogId: number;
}

export const useDeployChangelog = ({ changelogId }: UseDeployChangelogParams) => {
  const { workspaceId } = useParams();
  const authContext = useAuth();
  const queryClient = useQueryClient();

  return useMutation<DeployChangelogResponse, Error, DeployChangelogRequest>({
    mutationFn: async (_request: DeployChangelogRequest) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 35000);

      try {
        const response = await fetch(
          `${API_URL}/api/dashboard/v2/workspaces/${workspaceId}/changelogs/${changelogId}/deploy`,
          {
            method: "POST",
            headers: jsonAuthHeaders(authContext?.userToken ?? ""),
            body: JSON.stringify({}),
            signal: controller.signal,
          },
        );

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || "Failed to deploy change");
        }

        return response.json();
      } catch (error) {
        clearTimeout(timeoutId);
        throw error;
      }
    },
    onSuccess: data => {
      // Invalidate changelog queries to refresh the list
      queryClient.invalidateQueries(["global-changelog", workspaceId]);
      queryClient.invalidateQueries(["changelog"]);
      queryClient.invalidateQueries(["changelog-pending-count", workspaceId]);

      // Refetch label-related queries for version selectors to ensure immediate update
      // For prompt labels and versions
      queryClient.refetchQueries({
        predicate: query => {
          const keyString = JSON.stringify(query.queryKey);
          return (
            keyString.includes("prompt-versions") || keyString.includes("dynamic-release-labels")
          );
        },
      });

      // For workflow labels and versions
      queryClient.refetchQueries({
        predicate: query => {
          const keyString = JSON.stringify(query.queryKey);
          return keyString.includes(ENDPOINTS.workflow_versions);
        },
      });
      queryClient.refetchQueries({
        queryKey: ["workflow-release-labels"],
      });
      queryClient.refetchQueries({
        queryKey: ["workflow-version-v2"],
      });

      // Show success toast
      displayToast(data.message || "Change deployed successfully", ToastType.success);

      // Trigger confetti celebration
      triggerConfetti();
    },
    onError: error => {
      displayErrorToast(error.message || "Failed to deploy change");
    },
  });
};
